import mm from 'moment';
import { API } from '@/actions/API';
import {
  formatApiErrorMessage,
  fDateTimeNormal,
  plannerTimelineInstantMs,
} from '@/actions/Helper';
import { PLANNER_CARD_DEFAULT_HEADER_HINT } from '@/components/AutobookPlanner/PlannerCardHeader';

function parseDistanceMiles(distanceField) {
  if (distanceField == null) return NaN;
  const n = parseFloat(String(distanceField).replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n : NaN;
}

/** Padding before first / after last event so the axis is not flush to badges (ms). */
const TIMELINE_AXIS_PADDING_MS = 2 * 60 * 60 * 1000;

/** Place each node along [0,1] using [earliest − 2h, latest + 2h] as the time axis. */
function assignPositionsByEventTimes(nodes) {
  const list = (nodes || []).map((n) => ({ ...n }));
  const valid = list.filter(
    (n) => n.atIso != null && plannerTimelineInstantMs(n.atIso) != null
  );
  if (valid.length === 0) {
    return list.map((n, i) => ({
      ...n,
      p: list.length <= 1 ? 0.5 : i / Math.max(list.length - 1, 1),
    }));
  }
  const times = valid.map((n) => plannerTimelineInstantMs(n.atIso));
  const tMin = Math.min(...times);
  const tMax = Math.max(...times);
  const axisStart = tMin - TIMELINE_AXIS_PADDING_MS;
  const axisEnd = tMax + TIMELINE_AXIS_PADDING_MS;
  const span = axisEnd - axisStart;

  const withP = list.map((n) => {
    const t = plannerTimelineInstantMs(n.atIso);
    if (t == null) return { ...n, p: 0.5 };
    if (span <= 0) return { ...n, p: 0.5 };
    const p = (t - axisStart) / span;
    return { ...n, p: Math.max(0, Math.min(1, p)) };
  });

  return withP.sort((a, b) => {
    const ta = plannerTimelineInstantMs(a.atIso) ?? 0;
    const tb = plannerTimelineInstantMs(b.atIso) ?? 0;
    return ta - tb;
  });
}

function toMillisSafe(raw) {
  const t = mm(raw).valueOf();
  return Number.isFinite(t) ? t : null;
}

/** Extra parses when API sends non-ISO strings (e.g. MM-DD-YYYY + time). */
const APPOINTMENT_PARSE_FORMATS = [
  ...(typeof mm.ISO_8601 !== 'undefined' ? [mm.ISO_8601] : []),
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DD HH:mm',
  'YYYY-MM-DDTHH:mm:ss.SSSSSSZ',
  'YYYY-MM-DDTHH:mm:ssZ',
  'YYYY-MM-DDTHH:mm:ss',
  'MM-DD-YYYY HH:mm',
  'MM-DD-YYYY HH:mm:ss',
  'MM-DD-YYYY h:mm A',
  'MM/DD/YYYY HH:mm',
  'MM/DD/YYYY hh:mm A',
  'YYYY-MM-DD',
  'MM-DD-YYYY',
];

function coerceParseableDatetime(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number' && Number.isFinite(v)) {
    return toMillisSafe(v) != null ? v : null;
  }
  if (toMillisSafe(v) != null) return v;

  const str = String(v).trim();
  const loose = mm(str, APPOINTMENT_PARSE_FORMATS, true);
  if (loose.isValid()) return loose.toISOString();

  const z = mm.parseZone(str);
  if (z.isValid()) return z.toISOString();

  return null;
}

function joinDateTimeBestEffort(dateStr, timeStr) {
  if (dateStr == null || timeStr == null) return null;
  const d = String(dateStr).trim();
  const t = String(timeStr).trim();
  if (!d || !t) return null;
  const candidates = [`${d} ${t}`, `${d}T${t}`];
  for (const c of candidates) {
    const coerced = coerceParseableDatetime(c);
    if (coerced != null) return coerced;
  }
  return null;
}

/** Laravel paginator vs plain array */
function flattenAppointmentList(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.data)) return raw.data;
  return [];
}

function collectPlannerAppointments(item) {
  const c = item?.container || {};
  const merged = [
    ...flattenAppointmentList(item?.appointments),
    ...flattenAppointmentList(c?.appointments),
  ];
  const seen = new Set();
  return merged.filter((a) => {
    if (!a || typeof a !== 'object') return false;
    const key = a.id != null ? `id:${a.id}` : a.uuid != null ? `u:${a.uuid}` : null;
    if (key) {
      if (seen.has(key)) return false;
      seen.add(key);
    }
    return true;
  });
}

function extractAppointmentAt(appt) {
  if (!appt || typeof appt !== 'object') return null;

  const directCandidates = [
    /** Live-unload planner API: primary slot strings */
    appt.taa_start_string,
    appt.taa_string,
    appt.appointment_at,
    appt.starts_at,
    appt.start_at,
    appt.booked_at,
    appt.scheduled_at,
    appt.slot_at,
    appt.time_at,
    appt.return_appointment,
    appt.datetime,
    appt.appointment_datetime,
    appt.start_time,
    appt.pivot?.starts_at,
    appt.pivot?.start_at,
    appt.pivot?.appointment_at,
    appt.pivot?.created_at,
    appt.created_at,
  ];
  for (const c of directCandidates) {
    if (c == null || c === '') continue;
    const coerced = coerceParseableDatetime(c);
    if (coerced != null) return coerced;
  }

  /** Terminal slot shape used across inventory / appointment APIs */
  const taa =
    appt.terminal_appointment_availability ||
    appt.terminalAppointmentAvailability;
  if (taa && typeof taa === 'object') {
    const fromNested = coerceParseableDatetime(taa.datetime);
    if (fromNested != null) return fromNested;

    const joined = joinDateTimeBestEffort(taa.date, taa.time_start || taa.time || taa.start);
    if (joined != null) return joined;

    const d = taa.date;
    if (d && (taa.time_start == null || taa.time_start === '')) {
      const dateOnly = coerceParseableDatetime(d);
      if (dateOnly != null) return dateOnly;
    }
  }

  if (Array.isArray(appt.reserve_history)) {
    for (const rh of appt.reserve_history) {
      if (!rh) continue;
      const joined = joinDateTimeBestEffort(rh.date, rh.time_start || rh.time);
      if (joined != null) return joined;
    }
  }

  return null;
}

function extractAppointmentEndAt(appt) {
  if (!appt || typeof appt !== 'object') return null;

  const directCandidates = [
    appt.taa_end_string,
    appt.ends_at,
    appt.end_at,
    appt.end_time,
    appt.terminal_appointment_availability?.time_end,
    appt.terminalAppointmentAvailability?.time_end,
  ];

  for (const c of directCandidates) {
    if (c == null || c === '') continue;
    const coerced = coerceParseableDatetime(c);
    if (coerced != null) return coerced;
  }

  const taa =
    appt.terminal_appointment_availability ||
    appt.terminalAppointmentAvailability;
  if (taa && typeof taa === 'object') {
    const joined = joinDateTimeBestEffort(
      taa.date,
      taa.time_end || taa.end || taa.time_finish
    );
    if (joined != null) return joined;
  }

  return null;
}

function formatAppointmentRangeLabel(startIso, endIso) {
  const s = mm(startIso);
  if (!s.isValid()) return fDateTimeNormal(startIso);
  const e = endIso != null ? mm(endIso) : null;
  if (e == null || !e.isValid()) return fDateTimeNormal(startIso);

  return `${s.format('MM-DD-YYYY HH:mm')}-${e.format('HH:mm')}`;
}

function parseTimelineWallMs(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;

  const str = String(raw).trim();
  if (!str) return null;
  const hasExplicitOffset = /([zZ]|[+\-]\d{2}:?\d{2})$/.test(str);
  if (hasExplicitOffset) {
    const zoned = mm.parseZone(str);
    if (zoned.isValid()) {
      /**
       * Timeline is rendered by "wall clock" labels (HH:mm) shown to user.
       * If we keep absolute epoch from an offset ISO (e.g. Z / +00:00),
       * browser local zone shifts node position (+/- timezone hours).
       * So we intentionally normalize to local wall time with same calendar+clock.
       */
      const wall = mm(
        zoned.format('YYYY-MM-DD HH:mm:ss'),
        'YYYY-MM-DD HH:mm:ss',
        true
      );
      if (wall.isValid()) return wall.valueOf();
    }
  }

  const localStrict = mm(
    str,
    [
      'YYYY-MM-DD HH:mm:ss',
      'YYYY-MM-DD HH:mm',
      'YYYY-MM-DDTHH:mm:ss',
      'YYYY-MM-DDTHH:mm',
      'MM-DD-YYYY HH:mm:ss',
      'MM-DD-YYYY HH:mm',
      'MM/DD/YYYY HH:mm:ss',
      'MM/DD/YYYY HH:mm',
    ],
    true
  );
  if (localStrict.isValid()) return localStrict.valueOf();

  const localLoose = mm(str);
  if (localLoose.isValid()) return localLoose.valueOf();
  return null;
}

function resolveAppointmentNodeTitle(appt) {
  const raw = String(
    appt?.source ||
      appt?.origin_source ||
      appt?.status_text ||
      ''
  ).toLowerCase();
  const ab =
    appt?.from_autobook === 1 ||
    appt?.from_autobook === true ||
    raw.includes('autobook');
  if (ab) {
    return 'Booked Apt (Autobook)';
  }
  if (raw.includes('external') || appt?.is_external === 1) {
    return 'Booked Apt (External)';
  }
  return 'Booked Apt (Manual)';
}

function parseBufferMinutes(v, fallback = 30) {
  if (v == null || v === '') return fallback;
  const n =
    typeof v === 'number' && Number.isFinite(v)
      ? v
      : parseFloat(String(v).replace(/[^\d.-]/g, ''));
  if (!Number.isFinite(n) || n < 0) return fallback;
  return n;
}

function resolveAppointmentBufferMinutes(appt, side = 'before') {
  const t = appt?.terminal && typeof appt.terminal === 'object' ? appt.terminal : {};
  const beforeKeys =
    side === 'before'
      ? [
          appt?.pregate_window_before,
          appt?.pre_gate_window_before,
          appt?.buffer_before_min,
          appt?.buffer_before_minutes,
          t?.pregate_window_before,
          t?.pre_gate_window_before,
        ]
      : [
          appt?.pregate_window_after,
          appt?.pre_gate_window_after,
          appt?.buffer_after_min,
          appt?.buffer_after_minutes,
          t?.pregate_window_after,
          t?.pre_gate_window_after,
        ];

  for (const raw of beforeKeys) {
    if (raw == null || raw === '') continue;
    const parsed = parseBufferMinutes(raw, NaN);
    if (Number.isFinite(parsed) && parsed >= 0) return parsed;
  }
  return 30;
}

/** Milestones only: show duration from API verbatim inside parentheses (e.g. `5h 2m`). */
function durationParenFromApi(raw) {
  if (raw == null) return '';
  const s = String(raw).trim();
  if (!s) return '';
  return ` (${s})`;
}

/**
 * Maps one API row from GET autobook-planners/live-unload to LiveUnloadPlannerCard props.
 */
export function mapLiveUnloadApiItemToCard(item) {
  const c = item?.container || {};
  const wh = c.warehouse || {};
  const customer = c.customer || {};
  const company = c.company || {};
  const spec = c.specification || {};

  const containerNo = item.container_string || c.name || '';
  const miles = parseDistanceMiles(item.distance);

  const deliveryAt = item.delivery_at;
  const unloadAt = item.unload_at;
  const targetAt = item.target_at;
  const windowStart = item.window_start;
  const windowEnd = item.window_end;
  const lfdAt = c.last_free_day ?? item.last_free_day ?? null;

  const deliveryLbl = fDateTimeNormal(deliveryAt);
  const unloadLbl = fDateTimeNormal(unloadAt);
  const targetLbl = fDateTimeNormal(targetAt);
  const wsLbl = fDateTimeNormal(windowStart);
  const weLbl = fDateTimeNormal(windowEnd);
  const deliveryMilestone = deliveryAt ? fDateTimeNormal(deliveryAt) : '';
  const unloadMilestoneBase = unloadAt ? fDateTimeNormal(unloadAt) : '';
  const targetMilestoneBase = targetAt ? fDateTimeNormal(targetAt) : '';
  const windowStartMilestone = windowStart ? fDateTimeNormal(windowStart) : '';
  const windowEndMilestone = windowEnd ? fDateTimeNormal(windowEnd) : '';
  const unloadDurationSuffix = durationParenFromApi(item.unload_duration);
  const targetDurationSuffix = durationParenFromApi(item.target_duration);
  const unloadMilestone = `${unloadMilestoneBase}${unloadDurationSuffix}`.trim();
  const targetMilestone = `${targetMilestoneBase}${targetDurationSuffix}`.trim();

  const nodes = [];
  if (windowStart) {
    nodes.push({
      id: `${item.id}-window-start`,
      atIso: windowStart,
      timelineMs: parseTimelineWallMs(windowStart),
      apiColor: '#F5C542',
      title: 'Autobook\nWindow Start',
      time: wsLbl,
    });
  }
  if (deliveryAt) {
    nodes.push({
      id: `${item.id}-delivery`,
      atIso: deliveryAt,
      timelineMs: parseTimelineWallMs(deliveryAt),
      apiColor: '#F5C542',
      title: 'Delivery',
      time: deliveryLbl,
    });
  }
  if (unloadAt) {
    nodes.push({
      id: `${item.id}-unload`,
      atIso: unloadAt,
      timelineMs: parseTimelineWallMs(unloadAt),
      apiColor: '#7DD3FC',
      title: 'Unload Done',
      time: unloadLbl,
    });
  }
  if (targetAt) {
    nodes.push({
      id: `${item.id}-target`,
      atIso: targetAt,
      timelineMs: parseTimelineWallMs(targetAt),
      apiColor: '#60A5FA',
      title: 'Target Return',
      time: targetLbl,
    });
  }
  if (windowEnd) {
    nodes.push({
      id: `${item.id}-window-end`,
      atIso: windowEnd,
      timelineMs: parseTimelineWallMs(windowEnd),
      apiColor: '#F5C542',
      title: 'Autobook\nWindow End',
      time: weLbl,
    });
  }
  if (lfdAt && plannerTimelineInstantMs(lfdAt) != null) {
    nodes.push({
      id: `${item.id}-lfd`,
      atIso: lfdAt,
      timelineMs: parseTimelineWallMs(lfdAt),
      title: 'LFD',
      time: fDateTimeNormal(lfdAt),
    });
  }
  const plannerAppointments = collectPlannerAppointments(item);
  if (plannerAppointments.length > 0) {
    plannerAppointments.forEach((appt, idx) => {
      /** Prefer raw API slot string so timeline ms matches {@link fDateTimeNormal} display. */
      const apptAtRaw =
        appt.taa_start_string != null &&
            String(appt.taa_start_string).trim() !== ''
            ? String(appt.taa_start_string).trim()
            : appt.taa_string != null && String(appt.taa_string).trim() !== ''
              ? String(appt.taa_string).trim()
              : extractAppointmentAt(appt);
      if (!apptAtRaw || plannerTimelineInstantMs(apptAtRaw) == null) return;
      const apptEndRaw =
        appt.taa_end_string != null && String(appt.taa_end_string).trim() !== ''
          ? String(appt.taa_end_string).trim()
          : extractAppointmentEndAt(appt);
      const apptStartMs = parseTimelineWallMs(apptAtRaw);
      const apptEndMs = parseTimelineWallMs(apptEndRaw);
      const safeEndIso =
        apptEndRaw && apptEndMs != null && apptStartMs != null && apptEndMs >= apptStartMs
          ? apptEndRaw
          : apptAtRaw;
      const safeEndMs =
        apptEndMs != null && apptStartMs != null && apptEndMs >= apptStartMs
          ? apptEndMs
          : apptStartMs;
      const title = resolveAppointmentNodeTitle(appt);
      const term = appt.terminal && typeof appt.terminal === 'object' ? appt.terminal : {};
      const terminalSlug =
        term.slug != null && String(term.slug).trim() !== ''
          ? String(term.slug).trim()
          : '';
      const appointmentBufferBeforeMin = resolveAppointmentBufferMinutes(
        appt,
        'before'
      );
      const appointmentBufferAfterMin = resolveAppointmentBufferMinutes(
        appt,
        'after'
      );
      nodes.push({
        id: `${item.id}-appt-${appt.id ?? idx}`,
        atIso: apptAtRaw,
        timelineMs: apptStartMs,
        appointmentStartIso: apptAtRaw,
        appointmentEndIso: safeEndIso,
        appointmentStartMs: apptStartMs,
        appointmentEndMs: safeEndMs,
        appointmentBufferBeforeMin,
        appointmentBufferAfterMin,
        title,
        ...(terminalSlug ? { subtitle: terminalSlug } : {}),
        time: formatAppointmentRangeLabel(apptAtRaw, safeEndIso),
      });
    });
  }

  const timelineNodes = assignPositionsByEventTimes(nodes);

  const nodesWithIso = nodes.filter(
    (n) => n.atIso != null && plannerTimelineInstantMs(n.atIso) != null
  );
  let timelineWindowStartLabel = wsLbl || '—';
  let timelineWindowEndLabel = weLbl || '—';
  if (nodesWithIso.length >= 1) {
    const times = nodesWithIso.map((n) => plannerTimelineInstantMs(n.atIso));
    const tMin = Math.min(...times);
    const tMax = Math.max(...times);
    const axisStartMs = tMin - TIMELINE_AXIS_PADDING_MS;
    const axisEndMs = tMax + TIMELINE_AXIS_PADDING_MS;
    timelineWindowStartLabel = fDateTimeNormal(mm(axisStartMs));
    timelineWindowEndLabel = fDateTimeNormal(mm(axisEndMs));
  }

  const warehouseName = wh.name || 'Warehouse';
  const cityLine = [wh.address, wh.postcode].filter(Boolean).join(' ');

  const statusLabel =
    item.status != null && String(item.status).trim() !== ''
      ? String(item.status)
      : item.booking_status || '';

  const bookingTag =
    item.booking_status != null && String(item.booking_status).trim() !== ''
      ? String(item.booking_status).replace(/_/g, ' ')
      : '';

  return {
    id: String(item.uuid ?? item.id ?? containerNo),
    distanceMiles: miles,
    searchContainers: containerNo ? [containerNo] : [],
    status: statusLabel,
    badges: [containerNo, bookingTag].filter(Boolean),
    headerHint: PLANNER_CARD_DEFAULT_HEADER_HINT,
    containerInfo: [
      { label: 'Customer', value: customer.name || '—' },
      { label: 'Warehouse', value: warehouseName },
      { label: 'City', value: cityLine || '—' },
      { label: 'Container', value: containerNo || '—' },
      {
        label: 'Size / Type',
        value: spec.size_type || genSize(spec) || '—',
      },
      { label: 'SSL', value: company.name || company.alias || '—' },
    ],
    milestones: [
      { label: 'Delivery', value: deliveryMilestone || '—' },
      { label: 'Unload Done', value: unloadMilestone || '—' },
      { label: 'Target Return', value: targetMilestone || '—' },
      { label: 'Window Start', value: windowStartMilestone || '—' },
      { label: 'Window End', value: windowEndMilestone || '—' },
      {
        label: 'Distance',
        value: item.distance != null ? String(item.distance) : '—',
      },
    ],
    createdAtIso: item.created_at || null,
    updatedAtIso: item.updated_at || null,
    timeline: {
      windowStartLabel: timelineWindowStartLabel,
      windowEndLabel: timelineWindowEndLabel,
      nodes: timelineNodes,
    },
    movementSegments: [
      { type: 'place', label: warehouseName },
      {
        type: 'leg',
        miles: Number.isFinite(miles) ? miles : undefined,
      },
      { type: 'place', label: 'Port' },
    ],
  };
}

function genSize(spec) {
  if (!spec || spec.size == null) return '';
  const size = spec.size;
  const name = spec.name || '';
  const model = spec.model || '';
  if (name && model) return `${size}${name}-${model}`;
  return String(size);
}

/**
 * GET api/autobook-planners/live-unload
 * @param {{ page?: number, distance?: number|string, container_number?: string }} params
 */
export async function fetchLiveUnloadPlanners(params = {}) {
  const { page = 1, distance, container_number } = params;
  const query = { page };
  if (distance != null && distance !== '') query.distance = distance;
  if (container_number != null && String(container_number).trim() !== '') {
    query.container_number = String(container_number).trim();
  }

  const res = await API.get('autobook-planners/live-unload', query);
  if (!res.ok) {
    throw new Error(formatApiErrorMessage(res.data));
  }

  const root = res.data?.data;
  const lu = root?.lu_planners;
  const rows = Array.isArray(lu?.data) ? lu.data : [];
  const lastPage = Number(lu?.last_page) || 1;
  const currentPage = Number(lu?.current_page) || 1;
  const cards = rows.map(mapLiveUnloadApiItemToCard);

  return {
    cards,
    lastPage,
    currentPage,
    meta: {
      total: root?.total,
      booked: root?.booked,
      appointed: root?.appointed,
      remaining: root?.remaining,
    },
  };
}
